import React from 'react';
import {
  Paper,
  Typography,
  Button,
  Box
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { dashboardTheme } from '../styles/dashboardTheme';
import WidgetInfoTooltip from './WidgetInfoTooltip';

const CompactCheckInWidget = ({ lastCheckIn, onCheckIn }) => {
  const hasCheckedInToday = () => {
    if (!lastCheckIn) return false;
    const lastCheckInDate = new Date(lastCheckIn.createdAt);
    const today = new Date();
    return (
      lastCheckInDate.getDate() === today.getDate() &&
      lastCheckInDate.getMonth() === today.getMonth() &&
      lastCheckInDate.getFullYear() === today.getFullYear()
    );
  };

  const moodEmojiMap = {
    1: { emoji: '😢', label: 'V.Low' },
    2: { emoji: '😔', label: 'Low' },
    3: { emoji: '😐', label: 'Neutral' },
    4: { emoji: '🙂', label: 'Good' },
    5: { emoji: '😊', label: 'Great' }
  };

  const anxietyEmojiMap = {
    1: { emoji: '😌', label: 'V.Calm' },
    2: { emoji: '🙂', label: 'Calm' },
    3: { emoji: '😐', label: 'Moderate' },
    4: { emoji: '😟', label: 'Anxious' },
    5: { emoji: '😰', label: 'V.Anxious' }
  };

  const moodRating = lastCheckIn?.moodRating || lastCheckIn?.mood || 3;
  const anxietyRating = lastCheckIn?.anxietyRating || 3;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1.5,
        height: '100%',
        minHeight: '160px',
        display: 'flex',
        flexDirection: 'column',
        background: dashboardTheme.colors.primary.gradient,
        color: 'white',
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: dashboardTheme.shadows.colored(dashboardTheme.colors.primary.main),
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: dashboardTheme.shadows.glow(dashboardTheme.colors.primary.main)
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Today's Check-in {hasCheckedInToday() && '✓'}
        </Typography>
        <WidgetInfoTooltip id="compactCheckIn" />
      </Box>

      {hasCheckedInToday() ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                Mood
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>
                  {moodEmojiMap[moodRating].emoji}
                </Typography>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.2 }}>
                    {moodEmojiMap[moodRating].label}
                  </Typography>
                  <Typography sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                    {moodRating}/5
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                Anxiety
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>
                  {anxietyEmojiMap[anxietyRating].emoji}
                </Typography>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.2 }}>
                    {anxietyEmojiMap[anxietyRating].label}
                  </Typography>
                  <Typography sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                    {anxietyRating}/5
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', textAlign: 'center', display: 'block', mb: 1 }}>
            {formatDistanceToNow(new Date(lastCheckIn.createdAt), { addSuffix: true })}
          </Typography>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={onCheckIn}
            sx={{
              background: 'rgba(255,255,255,0.9)',
              color: dashboardTheme.colors.primary.main,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.5,
              '&:hover': {
                background: 'rgba(255,255,255,1)'
              }
            }}
          >
            CHECK IN AGAIN
          </Button>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            How are you feeling today?
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
            {[1, 2, 3, 4, 5].map((value) => (
              <Box
                key={value}
                onClick={() => onCheckIn()}
                sx={{
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  padding: '4px',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.2)'
                  }
                }}
              >
                {moodEmojiMap[value].emoji}
              </Box>
            ))}
          </Box>
          
          <Button
            variant="contained"
            fullWidth
            onClick={onCheckIn}
            sx={{
              background: 'rgba(255,255,255,0.9)',
              color: dashboardTheme.colors.primary.main,
              fontWeight: 600,
              py: 1,
              '&:hover': {
                background: 'rgba(255,255,255,1)'
              }
            }}
          >
            CHECK IN NOW
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CompactCheckInWidget;